import type { ModelMessage, Tool } from "@tanstack/ai"

export type ContextExtraKind =
  | "product"
  | "variant"
  | "collection"
  | "collectionGroup"
  | "warehouse"
  | "channel"

export interface ContextExtra {
  kind: ContextExtraKind
  id: string
  name: string
}

export interface PageContext {
  page: string
  productId?: string
  productName?: string
  /** Single variant focus (only set when exactly one variant is selected). */
  variantId?: string
  variantName?: string
  /** All variants the user is currently viewing (subset of product's variants). */
  variantIds?: string[]
  collectionId?: string
  collectionName?: string
  collectionGroupId?: string
  collectionGroupName?: string
  warehouseId?: string
  warehouseName?: string
  channel?: string
  /** Set when viewing a Studio report — lets Haki propose tiles for "this report". */
  reportId?: string
  reportName?: string
  /** Set when viewing the Inbox page — lets Haki propose tiles for the Inbox pinboard. */
  isInboxPinboard?: boolean
  /** The date range the user currently has selected, as "YYYY-MM-DD". */
  startDate?: string
  endDate?: string
  /** True when the user is on the monthly view; weekly and monthly give different numbers. */
  monthly?: boolean
  /** User-attached extras (via the chip "+" picker) — sent alongside page-derived fields. */
  extras?: ContextExtra[]
}

/** How the user answered a tile proposal's card, stamped on by `recordActionResolution`. */
export interface ActionResolution {
  status: "done" | "cancelled"
  reportId?: number
  reportName?: string
  tileId?: number
  tileIds?: number[]
  /** `move_tile` only — the tile was clamped to a size the inbox grid allows. */
  sizeChanged?: boolean
}

/** An `action` entry inside `HakiMessage.metadata.blocks`. */
export interface PersistedActionBlock {
  blockType: "action"
  blockId: string
  data?: { actionKind?: string; payload?: Record<string, unknown> }
  resolution?: ActionResolution
}

/** A resolution flattened together with its proposal, ready to describe to the model. */
export interface ResolvedTileAction extends ActionResolution {
  actionKind: TileActionKind
  tileName?: string
  tileNames?: string[]
  target?: "inbox"
}

export const TILE_ACTION_KINDS = [
  "create_tile",
  "create_tiles",
  "update_tile",
  "move_tile",
  "move_tiles",
] as const

export type TileActionKind = (typeof TILE_ACTION_KINDS)[number]

export function isTileActionKind(value: unknown): value is TileActionKind {
  return TILE_ACTION_KINDS.some((kind) => kind === value)
}

export const EXPORT_ACTION_KIND = "export_excel"

/** "number" is the legacy name for "kpis". */
export const DATA_BLOCK_TYPES = ["table", "chart", "kpis", "matrix", "number"] as const

export function isDataBlockType(value: unknown): boolean {
  return DATA_BLOCK_TYPES.some((kind) => kind === value)
}

export type HakiActionProposalKind = TileActionKind | typeof EXPORT_ACTION_KIND

/** `answer` carries no id: it exports the message the card renders inside, unpersisted when the tool runs. */
export interface ExportProposalPayload {
  target: "answer" | "blocks" | "report" | "tile"
  reportId?: number
  tileId?: number
  blockIds?: string[]
  label?: string
  fileName?: string
}

export interface CustomNamingRow {
  systemName: string
  customName: string
  customNamePlural: string
}

export interface ExtraFieldRow {
  tableName: string
  fieldName: string
  pandasDataType: string
  userSetDisplayName: string | null
}

export interface ToolStatus {
  tool: string
  detail?: string
}

export interface HakiDebugInfo {
  model: string
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheCreationTokens: number
  /** Fraction of prompt tokens served from cache (0–1). See observability.HakiTurnTelemetry. */
  cacheHitRatio: number
  toolCalls: Array<{ name: string; durationMs: number; success: boolean }>
  totalLatencyMs: number
  timeToFirstTokenMs: number | null
  iterations: number
  retries: number
  fabricationGuardTriggered: boolean
  emptyResponseFallback: boolean
}

/** Routed follow-up suggestion. Parsed from `>>SUGGESTION:<KIND>:...` sentinel lines. */
export type TypedSuggestion =
  | { kind: "query"; text: string }
  | { kind: "navigate"; path: string; label: string }
  | { kind: "explain"; variantId: string; label: string }

export type StructuredBlockType = "table" | "chart" | "kpis" | "matrix" | "unavailable"

export type HakiStreamEvent =
  | { type: "message_text_delta"; content: string; fullContent: string }
  | { type: "message_text_done"; content: string }
  | { type: "thinking_delta"; content: string }
  | { type: "tool_status"; tool: string; detail?: string; input?: Record<string, unknown> }
  | {
      type: "block_committed"
      blockType: StructuredBlockType
      blockId: string
      data: unknown
      afterLength?: number
      fromQueryRef?: boolean
    }
  | { type: "block_rejected"; blockId: string; reason: string }
  | {
      type: "action_proposed"
      blockId: string
      actionKind: HakiActionProposalKind
      payload: Record<string, unknown>
      afterLength?: number
    }
  | { type: "sources_committed"; sources: Array<{ type: string; label: string; id?: string }> }
  | { type: "followups_committed"; followups: TypedSuggestion[] }
  | {
      type: "message_done"
      model: string
      totalTokens: number
      langfuseTraceId: string
      debugInfo?: HakiDebugInfo
    }
  | { type: "error"; content: string; detail?: string }
  // Router-only: full message chain for this turn (assistant + tool calls + tool results).
  // Never forwarded to the client — intercepted by router.ts to persist contentBlocks.
  | { type: "turn_content_blocks"; messages: HakiMessage[] }

export type HakiClientStreamEvent = Exclude<HakiStreamEvent, { type: "turn_content_blocks" }>

// The subset of events a tool execution can emit mid-turn (render tools, propose-action
// tools) — buffered by runtime.ts and drained between provider chunks, as opposed to events
// the model/provider stream itself produces.
export type HakiToolBlockEvent = Extract<
  HakiStreamEvent,
  { type: "block_committed" | "block_rejected" | "action_proposed" }
>

// Vendor-neutral message + tool types, sourced from TanStack AI. `ModelMessage` carries the
// full turn shape (role, content, toolCalls, toolCallId, thinking) that we round-trip through
// the DB `contentBlocks` column; `Tool` covers our custom function tools (server-side provider
// tools like web_search live in ai/provider.ts, not here).
export type HakiMessage = ModelMessage
export type HakiTool = Tool

export interface AiSettings {
  /** When false, Haki cannot read tenant data (run_query_spec, search_entities disabled, page context stripped). */
  dataAccess: boolean
  /** When false, web_search/web_fetch are not exposed. */
  webSearch: boolean
}

/** Per-user response tone preference for Haki. Controls prose style only — not tools, charts, or suggestions. */
export type HakiTone = "concise" | "balanced" | "conversational"
